/**
 * Focus model for the screens that put a **list inside a focusable container** — Search's result
 * scroller and the Playlist track list (docs/04 §2.1, QA UX-045 / UX-046).
 *
 * A norigin focusable container is a focus *target* like any other: `smartNavigate` picks it as a
 * sibling and `setFocus` then drills into its children. When it has **no** focusable children —
 * the query is empty, the request is still in flight, the tab has no results — the drill-down has
 * nowhere to go and the focus is parked on the container itself. The container is not a managed
 * leaf (it renders no `data-focus-key`), so from that moment on:
 *
 * - the watchdog sees `out_of_scope` and repairs on **every** press (the "Foco reparado N×" chips),
 * - `document` holds zero — or, mid-render, two — `data-focused` nodes, breaking Carlos's T1 rule,
 * - and the remote is dead: on Search the query never grew past its first letter.
 *
 * Two rules keep it honest, and both live here so they can be unit tested without a DOM:
 *
 * 1. {@link listIsFocusable} — a container is a focus target only while it actually holds rows.
 *    With no rows the D-pad walks straight past it (Search: Down from the tab strip lands on the
 *    keyboard) instead of falling into it.
 * 2. {@link listFocusRepair} — when the rows change *under* the focus (a keystroke restarts the
 *    query, a tab switch empties the list, the list shrinks below the focused index), it says
 *    where the focus has to go: back to the keyboard, or onto the last row that still exists.
 */

export interface ListFocusScope {
  /** Focus key of the container (`search-results`, `playlist-list`). */
  containerKey: string;
  /** Prefix of the row keys, including the trailing dash (`search-result-`). */
  rowPrefix: string;
  /**
   * Where the focus goes when the list empties. A *container* key is fine and preferred: norigin
   * drills into its `lastFocusedChild`, so Search returns the user to the very key they typed.
   */
  fallbackKey: string;
  /**
   * Focus key of the "load more" control rendered *after* the last row (UX-047), when the list
   * has one. It is a row of the list as far as the focus is concerned: it lives inside the
   * container, and it disappears — taking the focus with it — the moment the last page lands.
   */
  moreKey?: string;
}

/** Focus key of Search's "Ver más" row (UX-047). */
export const SEARCH_MORE_FOCUS = 'search-more';

export const SEARCH_LIST: ListFocusScope = {
  containerKey: 'search-results',
  rowPrefix: 'search-result-',
  // The on-screen keyboard container: `saveLastFocusedChild` puts the user back on their letter.
  fallbackKey: 'osk',
  moreKey: SEARCH_MORE_FOCUS,
};

export const PLAYLIST_LIST: ListFocusScope = {
  containerKey: 'playlist-list',
  rowPrefix: 'playlist-row-',
  fallbackKey: 'playlist-back',
};

/** A container is a focus target only while it holds at least one row. */
export function listIsFocusable(rowCount: number): boolean {
  return rowCount > 0;
}

/** The index a row key carries, or `null` when the key is not one of this list's rows. */
export function rowIndexOf(key: string | null | undefined, scope: ListFocusScope): number | null {
  if (!key || !key.startsWith(scope.rowPrefix)) return null;
  const rest = key.slice(scope.rowPrefix.length);
  if (!/^\d+$/.test(rest)) return null;
  return Number(rest);
}

/**
 * True when the focus is inside this list: on a row, on the "load more" control, or parked on
 * the bare container.
 */
export function isInList(key: string | null | undefined, scope: ListFocusScope): boolean {
  return (
    key === scope.containerKey ||
    (scope.moreKey !== undefined && key === scope.moreKey) ||
    rowIndexOf(key, scope) !== null
  );
}

export interface ListFocusState {
  /** Whether the "load more" control is still rendered (UX-047). Defaults to false. */
  hasMore?: boolean;
}

/**
 * Where the focus must move now that the list holds `rowCount` rows, or `null` when the current
 * key is still a fine place to be. Only ever moves a focus that is *inside* the list: a user on
 * the keyboard or the tab strip is never dragged away by a response arriving.
 */
export function listFocusRepair(
  current: string | null | undefined,
  rowCount: number,
  scope: ListFocusScope,
  state: ListFocusState = {},
): string | null {
  if (!isInList(current, scope)) return null;
  if (rowCount <= 0) return scope.fallbackKey;
  // On "Ver más" (UX-047): fine while it is rendered; the moment the last page lands it is
  // unmounted, and the focus has to come back to the list instead of dying on a gone node.
  if (scope.moreKey !== undefined && current === scope.moreKey) {
    return state.hasMore ? null : `${scope.rowPrefix}${rowCount - 1}`;
  }
  const index = rowIndexOf(current, scope);
  // Parked on the bare container although rows exist: take the first one.
  if (index === null) return `${scope.rowPrefix}0`;
  if (index >= rowCount) return `${scope.rowPrefix}${rowCount - 1}`;
  return null;
}
